/**
 * Authentication Context for PetMas
 *
 * Handles customer authentication with JWT tokens stored in localStorage.
 * Includes automatic token refresh before expiration.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';
import {
  apiClient,
  AUTH_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  TOKEN_EXPIRES_KEY,
  USER_DATA_KEY,
} from '../api';
import { getFingerprintHash } from '../services/fingerprint';
import type { Customer } from '@petmas/shared';
import { ONE_SECOND_MS } from '@petmas/shared';

// Constants
const REFRESH_BUFFER_MS = 60_000; // Refresh 1 minute before expiration
const TOKEN_PREVIEW_LENGTH = 20;

interface AuthContextType {
  customer: Customer | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: (idToken: string) => Promise<void>;
  logout: () => Promise<void>;
  register: (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
  }) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const refreshTimerRef = useRef<number | null>(null);

  // Set up automatic token refresh timer
  const setupRefreshTimer = () => {
    // Clear any existing timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    const expiresAt = localStorage.getItem(TOKEN_EXPIRES_KEY);
    if (!expiresAt) {
      console.log('[AuthContext] No expiration time found - skipping refresh timer');
      return;
    }

    const expirationTime = parseInt(expiresAt, 10);
    const now = Date.now();
    const timeUntilExpiration = expirationTime - now;
    const timeUntilRefresh = timeUntilExpiration - REFRESH_BUFFER_MS;

    console.log('[AuthContext] Setting up refresh timer:', {
      expiresAt: new Date(expirationTime).toISOString(),
      timeUntilExpiration: Math.floor(timeUntilExpiration / ONE_SECOND_MS) + 's',
      timeUntilRefresh: Math.floor(timeUntilRefresh / ONE_SECOND_MS) + 's',
    });

    // If token expires soon, refresh immediately
    if (timeUntilRefresh <= 0) {
      console.log('[AuthContext] Token expires soon - refreshing immediately');
      void refreshAccessToken();
      return;
    }

    // Schedule refresh before expiration
    refreshTimerRef.current = window.setTimeout(() => {
      console.log('[AuthContext] Refresh timer triggered - refreshing token');
      void refreshAccessToken();
    }, timeUntilRefresh);
  };

  // Refresh access token using refresh token
  const refreshAccessToken = async () => {
    try {
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (!refreshToken) {
        console.error('[AuthContext] No refresh token available');
        await logout();
        return;
      }

      console.log('[AuthContext] Refreshing access token...');
      const response = await apiClient.refreshToken(refreshToken);

      // Update tokens
      localStorage.setItem(AUTH_TOKEN_KEY, response.accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, response.refreshToken);
      localStorage.setItem(TOKEN_EXPIRES_KEY, response.expiresAt.toString());

      console.log('[AuthContext] Token refreshed successfully');

      // Set up next refresh timer
      setupRefreshTimer();
    } catch (error) {
      console.error('[AuthContext] Token refresh failed:', error);
      await logout();
    }
  };

  useEffect(() => {
    const validateTokenOnMount = async () => {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      if (!token) {
        setIsLoading(false);
        return;
      }

      // Load cached customer data for immediate rendering
      const userData = localStorage.getItem(USER_DATA_KEY);
      if (userData) {
        try {
          setCustomer(JSON.parse(userData));
        } catch {
          console.warn('[AuthContext] Invalid userData in localStorage - clearing');
          localStorage.removeItem(USER_DATA_KEY);
        }
      }

      // Validate token by calling the /auth/me endpoint
      try {
        console.log('[AuthContext] Validating token on mount...');
        const freshCustomer = await apiClient.getCurrentCustomer();

        // Update state and localStorage with fresh data
        setCustomer(freshCustomer);
        localStorage.setItem(USER_DATA_KEY, JSON.stringify(freshCustomer));
        console.log('[AuthContext] Token validated successfully');

        // Set up automatic refresh timer
        setupRefreshTimer();
      } catch (error) {
        // Token validation failed - clear auth state
        console.error('[AuthContext] Token validation failed:', error);
        localStorage.removeItem(AUTH_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem(TOKEN_EXPIRES_KEY);
        localStorage.removeItem(USER_DATA_KEY);
        setCustomer(null);
      } finally {
        setIsLoading(false);
      }
    };

    void validateTokenOnMount();

    // Clean up timer on unmount
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, []);

  // Set up automatic logout handler for 401 responses
  useEffect(() => {
    const handleUnauthorized = () => {
      console.log('[AuthContext] Received 401 Unauthorized - clearing auth state');
      localStorage.removeItem(AUTH_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(TOKEN_EXPIRES_KEY);
      localStorage.removeItem(USER_DATA_KEY);
      setCustomer(null);
    };

    apiClient.onUnauthorized = handleUnauthorized;

    return () => {
      apiClient.onUnauthorized = null;
    };
  }, []);

  const login = async (email: string, password: string) => {
    const response = await apiClient.login(email, password);
    console.log('[AuthContext] Login response:', {
      hasToken: !!response.accessToken,
      tokenPreview: response.accessToken
        ? `${response.accessToken.substring(0, TOKEN_PREVIEW_LENGTH)}...`
        : 'null',
      hasRefreshToken: !!response.refreshToken,
      expiresAt: new Date(response.expiresAt).toISOString(),
      customer: response.customer,
    });

    // Store all authentication data
    localStorage.setItem(AUTH_TOKEN_KEY, response.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, response.refreshToken);
    localStorage.setItem(TOKEN_EXPIRES_KEY, response.expiresAt.toString());
    localStorage.setItem(USER_DATA_KEY, JSON.stringify(response.customer));
    console.log('[AuthContext] Tokens stored in localStorage');

    setCustomer(response.customer as Customer);

    // Merge anonymous profile with authenticated customer (fire-and-forget)
    try {
      const fingerprintHash = await getFingerprintHash();
      const mergeResult = await apiClient.mergeCustomerProfile(
        fingerprintHash,
        response.customer.id,
        response.customer.email
      );
      console.log('[AuthContext] Profile merge result:', mergeResult);
    } catch (error) {
      // Non-blocking - don't fail login if merge fails
      console.warn('[AuthContext] Profile merge failed:', error);
    }

    // Set up automatic refresh timer
    setupRefreshTimer();
  };

  const loginWithGoogle = async (idToken: string) => {
    const response = await apiClient.loginWithGoogle(idToken);
    console.log('[AuthContext] Google login response:', {
      hasToken: !!response.accessToken,
      hasRefreshToken: !!response.refreshToken,
      expiresAt: new Date(response.expiresAt).toISOString(),
      customer: response.customer,
    });

    // Store all authentication data
    localStorage.setItem(AUTH_TOKEN_KEY, response.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, response.refreshToken);
    localStorage.setItem(TOKEN_EXPIRES_KEY, response.expiresAt.toString());
    localStorage.setItem(USER_DATA_KEY, JSON.stringify(response.customer));
    console.log('[AuthContext] Google login tokens stored in localStorage');

    setCustomer(response.customer as Customer);

    // Merge anonymous profile with authenticated customer (fire-and-forget)
    try {
      const fingerprintHash = await getFingerprintHash();
      const mergeResult = await apiClient.mergeCustomerProfile(
        fingerprintHash,
        response.customer.id,
        response.customer.email
      );
      console.log('[AuthContext] Profile merge result:', mergeResult);
    } catch (error) {
      // Non-blocking - don't fail login if merge fails
      console.warn('[AuthContext] Profile merge failed:', error);
    }

    // Set up automatic refresh timer
    setupRefreshTimer();
  };

  const logout = async () => {
    // Clear refresh timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    try {
      await apiClient.logout();
    } catch {
      // Ignore logout errors
    }

    // Clear all authentication data
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRES_KEY);
    localStorage.removeItem(USER_DATA_KEY);
    setCustomer(null);
  };

  const register = async (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
  }) => {
    await apiClient.register(data);
    // After registration, user needs to verify email and then login
  };

  return (
    <AuthContext.Provider
      value={{
        customer,
        isAuthenticated: !!customer,
        isLoading,
        login,
        loginWithGoogle,
        logout,
        register,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
